import { debounce } from 'lodash-es'
import { useMemo, useRef, useState } from 'react'
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'

const DELAY_MS = 200

export const asyncRequest = async <T>(
    url: string | Request,
    options: RequestInit = {},
    controller?: AbortController,
): Promise<T> => {
    const res = await fetch(url, { ...options, signal: controller?.signal })
    const data = (await res.json()) as T
    return data
}

export type TFetchProps = { url?: string | Request; options?: RequestInit }

const requestData = async <TData>(
    { url, options }: TFetchProps,
    abortController: AbortController,
    setDataCallback: (data: TData) => void,
) => {
    try {
        const res = await asyncRequest<TData>(url, options, abortController)
        setDataCallback(res)
    } catch (e) {
        if (process.env.NODE_ENV === 'development') {
            console.warn(e)
        }
    }
}

/**
 * Returns fetch response and a function to fetch data.
 * @example
 * const [data, fetchData] = useFetch<TData>()
 */
const useFetch = <TData>({ url, options = {} }: TFetchProps = {}) => {
    const [data, setData] = useState<TData | undefined>()
    const controllerRef = useRef<AbortController>(undefined)

    const fetchData = useMemo(
        () =>
            debounce(
                (requestOptions: TFetchProps) =>
                    requestData(requestOptions, controllerRef.current, setData),
                DELAY_MS,
            ),
        [],
    )

    useIsomorphicLayoutEffect(() => {
        if (url) {
            controllerRef.current = new AbortController()
            requestData({ url, options }, controllerRef.current, setData)
        }
        return () => {
            fetchData.cancel()
            controllerRef?.current?.abort()
        }
    }, [fetchData, url])

    return [data, fetchData] as const
}

export default useFetch
